import React, { useCallback } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { Stack } from '@lyearn/ui';
import * as ContentList from '@/components/ContentList';
import AddStaffRolesModal from '@/modules/corporate/modules/staff-roles/components/AddStaffRolesModal';
import useStaffRolesWithRoleProfile from '@/modules/corporate/modules/staff-roles/views/StaffRoleManager/hooks/useStaffRolesWithRoleProfile';
import { RoleStatusEnum } from '@/types/schema';

import ListItem from './ListItem';

interface SpecificRolesListProps {
  contentName: string;
  staffRoleIds: string[];
  onChange: (val: string[]) => void;
}

function SpecificRolesList(props: SpecificRolesListProps) {
  const { t } = useTranslation();
  const { contentName, staffRoleIds, onChange } = props;
  const [open, { toggle }] = useToggle(false);
  const { staffRoles } = useStaffRolesWithRoleProfile({
    filters: {
      roleIds: staffRoleIds,
      status: [RoleStatusEnum.Active],
    },
    pause: !staffRoleIds.length,
  });

  const resolvedStaffRoles = staffRoleIds.length ? staffRoles : [];

  const onStaffRolesChange = useCallback(
    (nextRoleIds: any) => {
      onChange(nextRoleIds);
      toggle();
    },
    [onChange, toggle],
  );

  const onRemoveStaffRole = useCallback(
    (removedRoleId: any) => {
      onChange(staffRoleIds.filter((staffRoleId) => staffRoleId !== removedRoleId));
    },
    [staffRoleIds, onChange],
  );

  return (
    <ContentList.Provider items={resolvedStaffRoles}>
      <Stack className={resolvedStaffRoles.length ? 'mt-16' : undefined} direction="col">
        <ContentList.Items
          renderer={(index) => {
            const staffRole = resolvedStaffRoles[index];

            return (
              <ListItem key={staffRole.id} staffRole={staffRole} onRemove={onRemoveStaffRole} />
            );
          }}
        />
      </Stack>
      <ContentList.AddBlock
        className="mt-16 w-full"
        title={t('AssignmentRestriction:AddStaffRoles')}
        onClick={toggle}
      />
      {open ? (
        <AddStaffRolesModal
          contentName={contentName}
          staffRoleIds={staffRoleIds}
          onChange={onStaffRolesChange}
          onClose={toggle}
        />
      ) : null}
    </ContentList.Provider>
  );
}

export default SpecificRolesList;
